import React, { useContext } from 'react';
import {
    Icon,
    IconButton,
    MessageBar,
    MessageBarType,
    Stack,
    Text,
    TooltipHost,
} from '@fluentui/react';
import { ThemeContext } from '../../../../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../../../../../Common/util/localStorageUtils';
import {
    getStatusIconProperties,
    getTimelineStepText,
    isFinalStepStatus,
    TimelineDependency,
} from '../../../../models/Timeline';
import { StatusBadge } from '../../../../../Common/components/StatusBadge';
import {
    dateLocaleStringWithTimeZone,
    calculateTimeDifference,
} from '../../../../../Common/util/DateUtils';
import { ExpandableTextBox } from '../../../../../Common/components/ExpandableTextBox';
import {
    getConfig,
    GlobalConfigProperties,
} from '../../../../../Common/api/ABHub/ABHubGlobalConfig';

// Build names are one unbreakable token, so truncate instead of widening the flyout.
const truncatedDependencyNameStyles: React.CSSProperties = {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

interface IFlightDataDisplay {
    name: string;
    cloud: string;
    description: string;
    pendingName?: string;
    pendingDescription?: string;
    timestamp: string;
    status: string;
    statusDetails: string;
    isProjected?: boolean;
    isValid?: boolean;
    shouldDisplay: boolean;
    index?: number;
    inProgressIndex?: number;
    previousTimestamp?: string;
    hoveredSLA?: 'SRM' | 'CTS' | 'Customer' | null;
    stageType?: 'SRM' | 'CTS' | 'Customer' | null;
    isLast?: boolean;
    willNotRun?: boolean;
    allSubsequentItemsLackTimestamp?: boolean;
    dependencies?: TimelineDependency[];
    onDependencyClick?: (uniqueIdentifier: string) => void;
}

export const FlightDataDisplay: React.FC<IFlightDataDisplay> = (
    props: IFlightDataDisplay
) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);

    const isValidHyperlink = (input: string) => {
        const regex =
            /^(?:(?:https?|ftp):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:\/\S*)?$/i;
        return regex.test(input);
    };

    const copyToClipboard = (data?: string) => {
        if (data) {
            navigator.clipboard.writeText(data);
        }
    };

    // Not gated on the status being NotStarted: the running step is often reported as Processing.
    // The timestamp check keeps a stale step from claiming the marker when later steps have moved on.
    const isInProgress =
        props.index === props.inProgressIndex &&
        props.allSubsequentItemsLackTimestamp === true;
    const isComplete = ['Completed', 'Inherited', 'Transferred'].includes(
        props.status
    );
    const isTerminal = isFinalStepStatus(props.status);

    const { name: displayName, description: displayDescription } =
        getTimelineStepText(
            props.name,
            props.description,
            isInProgress,
            isTerminal,
            props.pendingName,
            props.pendingDescription
        );

    // Upcoming steps have no display description, so describe what the step will do.
    const tooltipDescription =
        displayDescription ||
        props.pendingDescription ||
        props.description ||
        'No description available.';

    // Calculate duration from previous step
    const duration =
        props.previousTimestamp &&
        props.timestamp &&
        (isComplete || isTerminal || props.inProgressIndex === props.index)
            ? calculateTimeDifference(props.previousTimestamp, props.timestamp)
            : null;

    // Determine if this item should be highlighted based on SLA hover
    const shouldHighlight = props.hoveredSLA === props.stageType;

    let statusLabel = 'Upcoming';
    let iconName = getStatusIconProperties(props.status, theme).iconName;
    let iconColor =
        getStatusIconProperties(props.status, theme).iconColor ||
        theme.palette.themePrimary;

    if (isInProgress) {
        statusLabel = 'Processing';
        iconName = 'Clock';
        iconColor = theme.palette.themePrimary;
    } else if (props.status === 'Failed') {
        statusLabel = 'Failed';
    } else if (props.status === 'Cancelled') {
        statusLabel = 'Cancelled';
    } else if (isComplete) {
        statusLabel = 'Complete';
    } else if (props.willNotRun) {
        statusLabel = 'Will Not Run';
    }

    const stateClass = isInProgress
        ? 'is-in-progress'
        : props.status === 'Failed'
        ? 'is-failed'
        : props.status === 'Cancelled'
        ? 'is-cancelled'
        : isComplete
        ? 'is-complete'
        : props.willNotRun
        ? 'will-not-run'
        : 'is-upcoming';

    return (
        <div
            className={`FlightDataDisplay-root ${stateClass} ${
                props.isLast ? 'is-last' : ''
            }`}
        >
            {props.shouldDisplay && (
                <Stack
                    horizontal
                    tokens={{ childrenGap: 14 }}
                    style={{
                        alignItems: 'flex-start',
                        padding: shouldHighlight ? '10px 12px' : '8px 12px',
                        backgroundColor: shouldHighlight
                            ? theme.palette.themeLighterAlt
                            : 'transparent',
                        borderRadius: shouldHighlight ? 4 : 0,
                        border: shouldHighlight
                            ? `2px solid ${theme.palette.themePrimary}`
                            : 'none',
                        transition: 'all 0.2s ease',
                    }}
                >
                    <span className="FlightDataDisplay-route">
                        <span
                            className="FlightDataDisplay-marker"
                            style={{
                                backgroundColor:
                                    theme.semanticColors.bodyBackground,
                                borderColor: iconColor,
                            }}
                        >
                            <Icon
                                iconName={iconName}
                                style={{ color: iconColor, fontSize: 14 }}
                            />
                        </span>
                    </span>
                    <Stack
                        tokens={{ childrenGap: 2 }}
                        style={{ flex: 1, minWidth: 0 }}
                    >
                        <div
                            style={{
                                borderBottom: `1px solid ${theme.palette.neutralLight}`,
                                padding: '4px 0 10px',
                                marginBottom: 10,
                            }}
                        >
                            <Stack
                                horizontal
                                verticalAlign="center"
                                tokens={{ childrenGap: 4 }}
                            >
                                <Text
                                    variant="medium"
                                    style={{ fontWeight: 600 }}
                                >
                                    {displayName}
                                </Text>
                                <TooltipHost content={tooltipDescription}>
                                    <IconButton
                                        ariaLabel="Stage description"
                                        iconProps={{ iconName: 'Info' }}
                                        styles={{
                                            root: {
                                                color: theme.palette
                                                    .themePrimary,
                                                height: 24,
                                                width: 24,
                                            },
                                            rootHovered: {
                                                color: theme.palette.themeDark,
                                            },
                                        }}
                                    />
                                </TooltipHost>
                            </Stack>
                        </div>
                        <Stack
                            horizontal
                            wrap
                            tokens={{ childrenGap: 8 }}
                            style={{
                                alignItems: 'center',
                            }}
                        >
                            <span className="FlightDataDisplay-stateLabel">
                                {statusLabel}
                            </span>
                            {props.cloud && (
                                <StatusBadge
                                    text={props.cloud}
                                    variant="primary"
                                    size="small"
                                />
                            )}
                            {props.timestamp && (
                                <Text
                                    variant="small"
                                    style={{
                                        color: theme.palette.neutralDark,
                                        border: '1px solid transparent',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        fontWeight: 'bold',
                                        padding: '2px 0',
                                    }}
                                >
                                    {dateLocaleStringWithTimeZone(
                                        props.timestamp
                                    )}
                                </Text>
                            )}
                            {duration && (
                                <StatusBadge
                                    text={duration}
                                    variant="neutral"
                                    size="small"
                                />
                            )}
                        </Stack>

                        {props.dependencies && props.dependencies.length > 0 && (
                            <Stack
                                className="FlightDataDisplay-dependencies"
                                tokens={{ childrenGap: 6 }}
                            >
                                {props.dependencies.map((dependency) => (
                                    <Stack
                                        key={`${dependency.dependencyType}-${dependency.uniqueIdentifier}`}
                                        horizontal
                                        tokens={{ childrenGap: 8 }}
                                        className="FlightDataDisplay-dependency"
                                    >
                                        <Icon
                                            iconName={
                                                getStatusIconProperties(
                                                    dependency.completionIndicator,
                                                    theme
                                                ).iconName
                                            }
                                            style={{
                                                color: getStatusIconProperties(
                                                    dependency.completionIndicator,
                                                    theme
                                                ).iconColor,
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Text
                                            variant="small"
                                            style={{
                                                flexShrink: 0,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {`${dependency.dependencyType}:`}
                                        </Text>
                                        <Text
                                            variant="small"
                                            title={dependency.displayName}
                                            style={{
                                                ...truncatedDependencyNameStyles,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {dependency.displayName}
                                        </Text>
                                    </Stack>
                                ))}
                            </Stack>
                        )}

                        {props.statusDetails?.trim() && (
                            <ExpandableTextBox
                                text={props.statusDetails}
                                title="Status Details"
                                theme={theme}
                                isValidHyperlink={isValidHyperlink}
                                onCopy={() =>
                                    copyToClipboard(props.statusDetails)
                                }
                                maxLength={200}
                            />
                        )}

                        {!props.statusDetails &&
                            props.status === 'Failed' &&
                            getConfig(
                                GlobalConfigProperties.Cloud
                            )?.toLowerCase() === 'public' && (
                                <MessageBar
                                    messageBarType={MessageBarType.warning}
                                    styles={{
                                        root: {
                                            borderLeft: `3px solid ${theme.semanticColors.errorIcon}`,
                                            marginTop: 4,
                                        },
                                        icon: {
                                            color: theme.semanticColors
                                                .errorIcon,
                                        },
                                    }}
                                >
                                    Details from {props.cloud} are NOT available
                                    in Public.
                                    <br />
                                    <strong>
                                        IMPORTANT: Create an IcM to the SRM team
                                        only AFTER your DRI has checked SRM
                                        Portal in {props.cloud}.
                                    </strong>
                                </MessageBar>
                            )}
                    </Stack>
                </Stack>
            )}
        </div>
    );
};
